<template>
  <!-- Mobile overlay -->
  <div
    v-if="!collapsed && isMobile"
    class="fixed inset-0 bg-black/50 z-40"
    @click="emit('toggle')"
  ></div>

  <!-- Sidebar -->
  <aside :class="sidebarClasses">
    <!-- Header -->
    <div class="sidebar-header">
      <div class="flex items-center gap-3">
        <img src="@/assets/logo.svg" alt="" class="logo" />
        <div v-if="!collapsed" class="logo-text">
          <h1>Garbal</h1>
          <p>Grupo del Valle</p>
        </div>
      </div>
      <button
        v-if="!collapsed || isMobile"
        @click="emit('toggle')"
        class="toggle-btn"
        aria-label="Contraer menú"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    </div>

    <!-- Region Selector -->
    <div v-if="regions.length > 0" class="region-selector">
      <label v-if="!collapsed" for="sidebar-region-select">Región activa</label>
      <div class="region-dropdown">
        <select
          id="sidebar-region-select"
          v-model="selectedRegion"
          @change="changeRegion"
          :aria-label="collapsed ? 'Región activa' : undefined"
        >
          <option v-for="region in regions" :key="region" :value="region">
            {{ getRegionName(region) }}
          </option>
        </select>
        <svg class="dropdown-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
        </svg>
      </div>
    </div>

    <!-- Navigation -->
    <nav class="sidebar-nav">
      <div v-if="!collapsed" class="nav-section">Mapas</div>

      <router-link
        v-for="item in filteredNavItems"
        :key="item.path"
        :to="item.enabled ? item.path : '#'"
        custom
        v-slot="{ navigate, isActive }"
      >
        <button
          @click="item.enabled ? navigate() : null"
          :class="[
            'nav-item',
            isActive && item.enabled ? 'nav-item--active' : '',
            !item.enabled ? 'nav-item--disabled' : ''
          ]"
          :title="collapsed ? item.label : (!item.enabled ? 'No disponible para esta region' : '')"
        >
          <div class="nav-icon" v-html="item.icon"></div>
          <span v-if="!collapsed" class="nav-label">{{ item.label }}</span>
          <span v-if="!collapsed && !item.enabled" class="nav-badge">N/D</span>
        </button>
      </router-link>

      <!-- Admin Section -->
      <template v-if="isAdmin">
        <div v-if="!collapsed" class="nav-section mt-6">Admin</div>

        <router-link to="/admin" custom v-slot="{ navigate, isActive }">
          <button
            @click="navigate"
            :class="['nav-item', isActive ? 'nav-item--active' : '']"
            :title="collapsed ? 'Administracion' : ''"
          >
            <div class="nav-icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
            </div>
            <span v-if="!collapsed" class="nav-label">Administracion</span>
          </button>
        </router-link>
      </template>
    </nav>

    <!-- User Profile -->
    <div class="sidebar-footer">
      <div class="user-profile">
        <div class="user-avatar">{{ userInitial }}</div>
        <div v-if="!collapsed" class="user-info">
          <p class="user-name">{{ userName }}</p>
          <p class="user-email">{{ userEmail }}</p>
        </div>
        <button
          @click="emit('logout')"
          class="logout-btn"
          title="Cerrar sesion"
          aria-label="Cerrar sesión"
        >
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Expand button when collapsed -->
    <button
      v-if="collapsed && !isMobile"
      @click="emit('toggle')"
      class="expand-btn"
      aria-label="Expandir menú"
    >
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 5l7 7-7 7M5 5l7 7-7 7"/>
      </svg>
    </button>
  </aside>

  <!-- Mobile menu button -->
  <button
    v-if="collapsed && isMobile"
    @click="emit('toggle')"
    class="mobile-menu-btn"
    aria-label="Abrir menú"
  >
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
    </svg>
  </button>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { useAreasStore } from "@/stores/areas";

export default {
  name: "Sidebar",
  props: {
    collapsed: {
      type: Boolean,
      default: false
    },
    userData: {
      type: Object,
      default: () => ({})
    },
    regions: {
      type: Array,
      default: () => []
    },
    currentRegion: {
      type: String,
      default: ""
    }
  },
  emits: ["toggle", "logout", "change-region"],
  setup(props, { emit }) {
    const areasStore = useAreasStore();
    const selectedRegion = ref(props.currentRegion);
    const isMobile = ref(false);

    // Check screen size
    const checkMobile = () => {
      isMobile.value = window.innerWidth < 1024;
    };

    onMounted(() => {
      checkMobile();
      window.addEventListener("resize", checkMobile);
    });

    onUnmounted(() => {
      window.removeEventListener("resize", checkMobile);
    });

    // Navigation items with icons
    const navItems = [
      {
        path: "/dashboard/agebs",
        label: "AGEBs",
        mapKey: "ageb",
        icon: `<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"/></svg>`
      },
      {
        path: "/dashboard/electoral",
        label: "Electoral",
        mapKey: "electoral",
        icon: `<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>`
      },
      {
        path: "/dashboard/index",
        label: "Indices",
        mapKey: "indices",
        icon: `<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"/></svg>`
      }
    ];

    // Computed
    const isAdmin = computed(() => props.userData?.admin === true);
    const userName = computed(() => props.userData?.name || "Usuario");
    const userEmail = computed(() => props.userData?.email || "");
    const userInitial = computed(() => userName.value.charAt(0).toUpperCase());

    const sidebarClasses = computed(() => [
      "sidebar",
      props.collapsed && !isMobile.value ? "sidebar--collapsed" : "",
      isMobile.value && !props.collapsed ? "sidebar--mobile-open" : "",
      isMobile.value && props.collapsed ? "sidebar--mobile-closed" : ""
    ]);

    // Filter nav items based on available maps for current region
    const filteredNavItems = computed(() => {
      const availableMaps = areasStore.currentAreaMaps;
      return navItems.map(item => ({
        ...item,
        enabled: availableMaps[item.mapKey] !== false
      }));
    });

    // Get region display name
    const getRegionName = (regionId) => {
      const areaData = areasStore.areasData[regionId];
      return areaData?.name || regionId.toUpperCase();
    };

    // Watch for external region changes
    watch(
      () => props.currentRegion,
      (val) => {
        selectedRegion.value = val;
      }
    );

    // Methods
    const changeRegion = () => {
      areasStore.setCurrentArea(selectedRegion.value);
      emit("change-region", selectedRegion.value);
    };

    return {
      selectedRegion,
      isMobile,
      navItems,
      filteredNavItems,
      isAdmin,
      userName,
      userEmail,
      userInitial,
      sidebarClasses,
      getRegionName,
      changeRegion,
      emit
    };
  }
};
</script>

<style scoped>
/* ==========================================
   SIDEBAR BASE — the ink spine
   ========================================== */

.sidebar {
  position: fixed;
  left: 0;
  top: 0;
  height: 100vh;
  height: 100dvh;
  width: 280px;
  background: #0d111d;
  border-right: 1px solid rgba(255,255,255,0.08);
  display: flex;
  flex-direction: column;
  z-index: 50;
  transition: transform 0.3s ease, width 0.3s ease;
}

.sidebar--collapsed {
  width: 76px;
}

.sidebar--mobile-closed {
  transform: translateX(-100%);
}

.sidebar--mobile-open {
  width: 280px;
  transform: translateX(0);
}

/* ==========================================
   HEADER
   ========================================== */

.sidebar-header {
  padding: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(255,255,255,0.08);
}

.logo {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
}

.logo-text h1 {
  color: #fff;
  font-family: "Fraunces", serif;
  font-size: 1.125rem;
  font-weight: 550;
  margin: 0;
  line-height: 1.1;
}

.logo-text p {
  color: #586284;
  font-size: 0.6875rem;
  letter-spacing: 0.04em;
  margin: 0.125rem 0 0;
}

.toggle-btn {
  padding: 0.5rem;
  background: transparent;
  border: none;
  color: #586284;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
}

.toggle-btn:hover {
  background: rgba(255,255,255,0.06);
  color: white;
}

/* ==========================================
   REGION SELECTOR — a legend entry
   ========================================== */

.region-selector {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid rgba(255,255,255,0.08);
}

.region-selector label {
  display: block;
  color: #586284;
  font-family: "IBM Plex Mono", monospace;
  font-size: 0.625rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 0.5rem;
}

.region-dropdown {
  position: relative;
}

.region-dropdown select {
  width: 100%;
  background: #1a2138;
  border: 1px solid #2c3552;
  color: white;
  padding: 0.7rem 2.5rem 0.7rem 0.9rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  appearance: none;
  transition: border-color 0.2s;
}

.region-dropdown select:hover {
  border-color: #3c4667;
}

.region-dropdown select:focus {
  outline: none;
  border-color: #8b5fbe;
}

.region-dropdown select option {
  background: #1a2138;
  color: white;
}

.dropdown-icon {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1.1rem;
  height: 1.1rem;
  color: #586284;
  pointer-events: none;
}

/* ==========================================
   NAVIGATION
   ========================================== */

.sidebar-nav {
  flex: 1;
  padding: 1rem 0.75rem;
  overflow-y: auto;
}

.nav-section {
  color: #586284;
  font-family: "IBM Plex Mono", monospace;
  font-size: 0.625rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 0.5rem 0.75rem;
  margin-bottom: 0.25rem;
}

.nav-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.7rem 0.75rem;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: #828caa;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  text-align: left;
  margin-bottom: 0.125rem;
}

.nav-item:hover:not(.nav-item--disabled) {
  background: rgba(255,255,255,0.04);
  color: white;
}

.nav-item--active {
  background: rgba(255,255,255,0.05) !important;
  color: #fff !important;
}

.nav-item--active::before {
  content: "";
  position: absolute;
  left: -0.75rem;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 1.1rem;
  background: #8b5fbe;
  border-radius: 0 2px 2px 0;
}

.sidebar--collapsed .nav-item--active::before {
  left: 0;
}

.nav-item--disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.nav-icon {
  width: 19px;
  height: 19px;
  flex-shrink: 0;
}

.nav-icon svg {
  width: 100%;
  height: 100%;
}

.nav-label {
  flex: 1;
}

.nav-badge {
  font-family: "IBM Plex Mono", monospace;
  font-size: 0.625rem;
  padding: 0.125rem 0.375rem;
  background: #2c3552;
  color: #828caa;
  border-radius: 4px;
  text-transform: uppercase;
}

/* ==========================================
   FOOTER
   ========================================== */

.sidebar-footer {
  padding: 1rem 1.25rem;
  border-top: 1px solid rgba(255,255,255,0.08);
}

.user-profile {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.user-avatar {
  width: 38px;
  height: 38px;
  background: #212a44;
  border: 1px solid #2c3552;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a47ecc;
  font-family: "Fraunces", serif;
  font-weight: 550;
  flex-shrink: 0;
}

.user-info {
  flex: 1;
  min-width: 0;
}

.user-name {
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-email {
  color: #586284;
  font-size: 0.75rem;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.logout-btn {
  padding: 0.5rem;
  background: transparent;
  border: none;
  color: #586284;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
}

.logout-btn:hover {
  background: rgba(176, 101, 47, 0.15);
  color: #c9834e;
}

.logout-btn svg {
  width: 20px;
  height: 20px;
}

/* ==========================================
   EXPAND BUTTON
   ========================================== */

.expand-btn {
  width: 100%;
  padding: 1rem;
  background: transparent;
  border: none;
  border-top: 1px solid rgba(255,255,255,0.08);
  color: #586284;
  cursor: pointer;
  transition: all 0.2s;
}

.expand-btn:hover {
  background: rgba(255,255,255,0.04);
  color: white;
}

.expand-btn svg {
  width: 20px;
  height: 20px;
  margin: 0 auto;
  display: block;
}

/* ==========================================
   MOBILE MENU BUTTON
   ========================================== */

.mobile-menu-btn {
  position: fixed;
  top: 1rem;
  left: 1rem;
  z-index: 40;
  width: 48px;
  height: 48px;
  background: #0d111d;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 10px;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(13,17,29,0.4);
}

.mobile-menu-btn svg {
  width: 24px;
  height: 24px;
}

/* ==========================================
   COLLAPSED STATE ADJUSTMENTS
   ========================================== */

.sidebar--collapsed .sidebar-header {
  justify-content: center;
}

.sidebar--collapsed .region-selector {
  padding: 0.75rem;
}

.sidebar--collapsed .region-dropdown select {
  padding: 0.5rem;
  text-align: center;
  font-size: 0.75rem;
}

.sidebar--collapsed .nav-item {
  justify-content: center;
  padding: 0.75rem;
}

.sidebar--collapsed .sidebar-footer {
  padding: 0.75rem;
}

.sidebar--collapsed .user-profile {
  justify-content: center;
}

.sidebar--collapsed .logout-btn {
  display: none;
}
</style>
